import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// TYPES
// ============================================================================

export type AccordionType = 'single' | 'multiple';
export type AccordionIconType = 'chevron' | 'plus' | 'none';

// ============================================================================
// ACCORDION CONTEXT
// ============================================================================

interface AccordionContextValue {
  type: AccordionType;
  expandedItems: string[];
  toggleItem: (itemId: string) => void;
  iconType: AccordionIconType;
  iconPosition: 'left' | 'right';
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null);

function useAccordionContext() {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error('Accordion components must be used within an Accordion');
  }
  return context;
}

// ============================================================================
// ACCORDION ICONS
// ============================================================================

interface AccordionIconProps {
  isExpanded: boolean;
  iconType: AccordionIconType;
  className?: string;
}

function AccordionIcon({ isExpanded, iconType, className }: AccordionIconProps) {
  if (iconType === 'none') return null;

  if (iconType === 'plus') {
    return (
      <svg
        className={clsx('w-4 h-4 transition-transform duration-200', className)}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        {isExpanded ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        )}
      </svg>
    );
  }

  // Default chevron
  return (
    <svg
      className={clsx(
        'w-4 h-4 transition-transform duration-200',
        isExpanded && 'rotate-180',
        className
      )}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ============================================================================
// ACCORDION ITEM CONTEXT
// ============================================================================

interface AccordionItemContextValue {
  itemId: string;
  isExpanded: boolean;
  isDisabled: boolean;
}

const AccordionItemContext = React.createContext<AccordionItemContextValue | null>(null);

function useAccordionItemContext() {
  const context = React.useContext(AccordionItemContext);
  if (!context) {
    throw new Error('AccordionTrigger and AccordionContent must be used within an AccordionItem');
  }
  return context;
}

// ============================================================================
// ACCORDION
// ============================================================================

export interface AccordionProps {
  children: React.ReactNode;
  type?: AccordionType;
  defaultExpanded?: string | string[];
  expanded?: string | string[];
  onExpandedChange?: (expanded: string | string[]) => void;
  iconType?: AccordionIconType;
  iconPosition?: 'left' | 'right';
  collapsible?: boolean;
  className?: string;
}

export function Accordion({
  children,
  type = 'single',
  defaultExpanded,
  expanded: controlledExpanded,
  onExpandedChange,
  iconType = 'chevron',
  iconPosition = 'right',
  collapsible = true,
  className,
}: AccordionProps) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState<string[]>(() => {
    if (defaultExpanded) {
      return Array.isArray(defaultExpanded) ? defaultExpanded : [defaultExpanded];
    }
    return [];
  });

  const isControlled = controlledExpanded !== undefined;
  const expandedItems = React.useMemo(() => {
    if (isControlled) {
      return Array.isArray(controlledExpanded) ? controlledExpanded : [controlledExpanded];
    }
    return uncontrolledExpanded;
  }, [isControlled, controlledExpanded, uncontrolledExpanded]);

  const toggleItem = React.useCallback(
    (itemId: string) => {
      let newExpanded: string[];

      if (type === 'single') {
        if (expandedItems.includes(itemId)) {
          newExpanded = collapsible ? [] : expandedItems;
        } else {
          newExpanded = [itemId];
        }
      } else {
        if (expandedItems.includes(itemId)) {
          newExpanded = expandedItems.filter((id) => id !== itemId);
        } else {
          newExpanded = [...expandedItems, itemId];
        }
      }

      if (!isControlled) {
        setUncontrolledExpanded(newExpanded);
      }

      if (onExpandedChange) {
        onExpandedChange(type === 'single' ? (newExpanded[0] || '') : newExpanded);
      }
    },
    [type, expandedItems, collapsible, isControlled, onExpandedChange]
  );

  const value = React.useMemo(
    () => ({
      type,
      expandedItems,
      toggleItem,
      iconType,
      iconPosition,
    }),
    [type, expandedItems, toggleItem, iconType, iconPosition]
  );

  return (
    <AccordionContext.Provider value={value}>
      <div className={clsx('divide-y divide-gray-200 dark:divide-gray-700', className)}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

// ============================================================================
// ACCORDION ITEM
// ============================================================================

export interface AccordionItemProps {
  children: React.ReactNode;
  value: string;
  disabled?: boolean;
  className?: string;
}

export function AccordionItem({
  children,
  value,
  disabled = false,
  className,
}: AccordionItemProps) {
  const { expandedItems } = useAccordionContext();
  const isExpanded = expandedItems.includes(value);

  const itemValue = React.useMemo(
    () => ({
      itemId: value,
      isExpanded,
      isDisabled: disabled,
    }),
    [value, isExpanded, disabled]
  );

  return (
    <AccordionItemContext.Provider value={itemValue}>
      <div
        className={clsx(
          'border-gray-200 dark:border-gray-700',
          disabled && 'opacity-50',
          className
        )}
        data-state={isExpanded ? 'open' : 'closed'}
        data-disabled={disabled || undefined}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

// ============================================================================
// ACCORDION TRIGGER
// ============================================================================

export interface AccordionTriggerProps {
  children: React.ReactNode;
  className?: string;
}

export function AccordionTrigger({ children, className }: AccordionTriggerProps) {
  const { toggleItem, iconType, iconPosition } = useAccordionContext();
  const { itemId, isExpanded, isDisabled } = useAccordionItemContext();

  const handleClick = () => {
    if (!isDisabled) {
      toggleItem(itemId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      className={clsx(
        'w-full flex items-center gap-3 py-4 px-4 text-left font-medium transition-colors',
        'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        isDisabled && 'cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent',
        iconPosition === 'left' && 'flex-row-reverse justify-end',
        className
      )}
      aria-expanded={isExpanded}
      aria-controls={`accordion-content-${itemId}`}
    >
      <span className="flex-1">{children}</span>
      <AccordionIcon isExpanded={isExpanded} iconType={iconType} />
    </button>
  );
}

// ============================================================================
// ACCORDION CONTENT
// ============================================================================

export interface AccordionContentProps {
  children: React.ReactNode;
  className?: string;
}

export function AccordionContent({ children, className }: AccordionContentProps) {
  const { itemId, isExpanded } = useAccordionItemContext();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (contentRef.current) {
      setHeight(isExpanded ? contentRef.current.scrollHeight : 0);
    }
  }, [isExpanded]);

  return (
    <div
      id={`accordion-content-${itemId}`}
      role="region"
      aria-labelledby={`accordion-trigger-${itemId}`}
      className={clsx(
        'overflow-hidden transition-all duration-200 ease-out',
        !isExpanded && 'h-0'
      )}
      style={{ height: height !== undefined ? height : undefined }}
    >
      <div ref={contentRef} className={clsx('px-4 pb-4 text-gray-600 dark:text-gray-400', className)}>
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// SIMPLE ACCORDION (All-in-one component)
// ============================================================================

export interface SimpleAccordionItem {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface SimpleAccordionProps {
  items: SimpleAccordionItem[];
  type?: AccordionType;
  defaultExpanded?: string | string[];
  expanded?: string | string[];
  onExpandedChange?: (expanded: string | string[]) => void;
  iconType?: AccordionIconType;
  iconPosition?: 'left' | 'right';
  collapsible?: boolean;
  className?: string;
  itemClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
}

export function SimpleAccordion({
  items,
  type = 'single',
  defaultExpanded,
  expanded,
  onExpandedChange,
  iconType = 'chevron',
  iconPosition = 'right',
  collapsible = true,
  className,
  itemClassName,
  triggerClassName,
  contentClassName,
}: SimpleAccordionProps) {
  return (
    <Accordion
      type={type}
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      iconType={iconType}
      iconPosition={iconPosition}
      collapsible={collapsible}
      className={className}
    >
      {items.map((item) => (
        <AccordionItem key={item.id} value={item.id} disabled={item.disabled} className={itemClassName}>
          <AccordionTrigger className={triggerClassName}>{item.title}</AccordionTrigger>
          <AccordionContent className={contentClassName}>{item.content}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

// ============================================================================
// FAQ ACCORDION (Specialized for FAQs)
// ============================================================================

export interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

export interface FAQAccordionProps {
  items: FAQItem[];
  type?: AccordionType;
  defaultExpanded?: string | string[];
  className?: string;
}

export function FAQAccordion({
  items,
  type = 'single',
  defaultExpanded,
  className,
}: FAQAccordionProps) {
  return (
    <Accordion
      type={type}
      defaultExpanded={defaultExpanded}
      iconType="plus"
      className={clsx('border border-gray-200 dark:border-gray-700 rounded-lg', className)}
    >
      {items.map((item, index) => (
        <AccordionItem
          key={item.id}
          value={item.id}
          className={clsx(
            index === 0 && 'rounded-t-lg',
            index === items.length - 1 && 'rounded-b-lg border-b-0'
          )}
        >
          <AccordionTrigger className="text-base">{item.question}</AccordionTrigger>
          <AccordionContent>{item.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

// ============================================================================
// COLLAPSIBLE (Single expandable section)
// ============================================================================

export interface CollapsibleProps {
  children: React.ReactNode;
  trigger: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  iconType?: AccordionIconType;
  iconPosition?: 'left' | 'right';
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
}

export function Collapsible({
  children,
  trigger,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  disabled = false,
  iconType = 'chevron',
  iconPosition = 'right',
  className,
  triggerClassName,
  contentClassName,
}: CollapsibleProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const handleToggle = () => {
    if (disabled) return;

    if (!isControlled) {
      setUncontrolledOpen(!isOpen);
    }
    onOpenChange?.(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  const contentRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div className={clsx('border border-gray-200 dark:border-gray-700 rounded-lg', className)}>
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center gap-3 py-3 px-4 text-left font-medium transition-colors rounded-lg',
          'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
          iconPosition === 'left' && 'flex-row-reverse justify-end',
          triggerClassName
        )}
        aria-expanded={isOpen}
      >
        <span className="flex-1">{trigger}</span>
        <AccordionIcon isExpanded={isOpen} iconType={iconType} />
      </button>

      <div
        className={clsx(
          'overflow-hidden transition-all duration-200 ease-out',
          !isOpen && 'h-0'
        )}
        style={{ height: height !== undefined ? height : undefined }}
      >
        <div
          ref={contentRef}
          className={clsx('px-4 pb-4 text-gray-600 dark:text-gray-400', contentClassName)}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// USE ACCORDION HOOK
// ============================================================================

export interface UseAccordionOptions {
  type?: AccordionType;
  defaultExpanded?: string | string[];
  collapsible?: boolean;
  onExpandedChange?: (expanded: string | string[]) => void;
}

export interface UseAccordionReturn {
  expandedItems: string[];
  isExpanded: (itemId: string) => boolean;
  toggle: (itemId: string) => void;
  expand: (itemId: string) => void;
  collapse: (itemId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
}

export function useAccordion(
  itemIds: string[],
  options: UseAccordionOptions = {}
): UseAccordionReturn {
  const {
    type = 'single',
    defaultExpanded,
    collapsible = true,
    onExpandedChange,
  } = options;

  const [expandedItems, setExpandedItems] = React.useState<string[]>(() => {
    if (defaultExpanded) {
      return Array.isArray(defaultExpanded) ? defaultExpanded : [defaultExpanded];
    }
    return [];
  });

  const isExpanded = React.useCallback(
    (itemId: string) => expandedItems.includes(itemId),
    [expandedItems]
  );

  const updateExpanded = React.useCallback(
    (newExpanded: string[]) => {
      setExpandedItems(newExpanded);
      if (onExpandedChange) {
        onExpandedChange(type === 'single' ? (newExpanded[0] || '') : newExpanded);
      }
    },
    [type, onExpandedChange]
  );

  const toggle = React.useCallback(
    (itemId: string) => {
      let newExpanded: string[];

      if (type === 'single') {
        if (expandedItems.includes(itemId)) {
          newExpanded = collapsible ? [] : expandedItems;
        } else {
          newExpanded = [itemId];
        }
      } else {
        if (expandedItems.includes(itemId)) {
          newExpanded = expandedItems.filter((id) => id !== itemId);
        } else {
          newExpanded = [...expandedItems, itemId];
        }
      }

      updateExpanded(newExpanded);
    },
    [type, expandedItems, collapsible, updateExpanded]
  );

  const expand = React.useCallback(
    (itemId: string) => {
      if (!expandedItems.includes(itemId)) {
        if (type === 'single') {
          updateExpanded([itemId]);
        } else {
          updateExpanded([...expandedItems, itemId]);
        }
      }
    },
    [type, expandedItems, updateExpanded]
  );

  const collapse = React.useCallback(
    (itemId: string) => {
      if (expandedItems.includes(itemId)) {
        if (collapsible || expandedItems.length > 1) {
          updateExpanded(expandedItems.filter((id) => id !== itemId));
        }
      }
    },
    [expandedItems, collapsible, updateExpanded]
  );

  const expandAll = React.useCallback(() => {
    if (type === 'multiple') {
      updateExpanded(itemIds);
    }
  }, [type, itemIds, updateExpanded]);

  const collapseAll = React.useCallback(() => {
    if (collapsible) {
      updateExpanded([]);
    }
  }, [collapsible, updateExpanded]);

  return {
    expandedItems,
    isExpanded,
    toggle,
    expand,
    collapse,
    expandAll,
    collapseAll,
  };
}
